import React from "react";
import { useGetProspectForStaffReviewQuery } from "common/types/gqlSchemaTypes";
import { Banner, Button, Flex, Link, LoadingIndicator, Modal, Text } from "@jsq/ds/core";
import { Form, FormCheckbox, FormTextarea } from "@jsq/ds/forms";
import { Field, type FieldProps } from "formik";
import { schema } from "./schema";
import { getContactStrings } from "./utils";
import styles from "./styles.scss";

interface Props {
  isSubmitting: boolean;
  subscriptionId: string;
  handleReject: (
    internalComments?: string,
    sendEmailToProspect?: boolean,
    emailComments?: string,
    isElectronicSignature?: boolean,
  ) => void;
  handleCloseModal: () => void;
}

interface FormValues {
  internalComments: string;
  sendEmailToProspect: boolean;
  emailComments: string;
}

export function RejectModal({
  isSubmitting,
  subscriptionId,
  handleReject,
  handleCloseModal,
}: Props): React.ReactElement<Props> {
  const { data, loading } = useGetProspectForStaffReviewQuery({
    variables: { subscriptionId },
    skip: !subscriptionId,
  });
  const [primaryContactStrings, secondaryContactStrings] = getContactStrings(
    data?.subscription?.prospect?.prospectContactMaps,
  );

  const handleSubmit = (formValues: FormValues) => {
    handleReject(
      formValues.internalComments,
      formValues.sendEmailToProspect,
      formValues.emailComments,
    );
  };

  return (
    <Modal show onHide={handleCloseModal} className={styles.SendBackModal__container}>
      <Modal.Header closeButton>
        <Modal.Title>Send back to prospect</Modal.Title>
      </Modal.Header>
      <Form
        initialValues={{ sendEmailToProspect: true, emailComments: "", internalComments: "" }}
        submitOnlyChangedValues={false}
        onSubmit={handleSubmit}
        validationSchema={schema}
      >
        <Modal.Body>
          {loading && <LoadingIndicator />}
          {!loading && data?.subscription && (
            <Flex flexDirection="column" gap={20}>
              <Banner
                variant="primary"
                title="What will happen to this subscription?"
                description={
                  <Text variant="secondary-md">
                    Send back the subscription package to allow the prospect to make changes to
                    their subscription information and sign the revised version.
                  </Text>
                }
                isDescriptionJsx
              />
              <Flex flexDirection="column" gap={4} alignItems="flex-start">
                <FormCheckbox
                  name="sendEmailToProspect"
                  label="Send custom email notification to prospect"
                  data-cy="send-email-check-box"
                />
                <Field name="emailComments">
                  {({ form }: FieldProps<string, FormValues>) => (
                    <FormTextarea
                      name="emailComments"
                      placeholder="Provide a reason for returning documents"
                      isFullWidth
                      size="small"
                      data-cy="email-comments-input"
                      disabled={!form.values.sendEmailToProspect}
                      data-1p-ignore
                    />
                  )}
                </Field>
                {/* TODO - help center link for See example */}
                <div>
                  <Text variant="secondary-sm">
                    This note will be included in an &lsquo;Action required&rsquo; email with a
                    direct link to edit their investor questionnaire.{" "}
                    <Link
                      href="https://help.junipersquare.com/hc/en-us/articles/17575174603923"
                      variant="muted"
                      hasUnderline
                      isInline
                      shouldOpenInNewTab
                    >
                      See example
                    </Link>
                  </Text>
                  {primaryContactStrings.length > 0 && (
                    <Text variant="secondary-sm">To: {primaryContactStrings}</Text>
                  )}
                  {secondaryContactStrings.length > 0 && (
                    <Text variant="secondary-sm">CC: {secondaryContactStrings}</Text>
                  )}
                </div>
              </Flex>
              <Flex flexDirection="column" gap={4}>
                <FormTextarea
                  name="internalComments"
                  label="Internal comments (optional)"
                  isFullWidth
                  size="x-small"
                />
                <Text variant="secondary-sm">Will not be shared with prospect</Text>
              </Flex>
            </Flex>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Flex justifyContent="flex-end">
            <Button
              variant="neutral"
              type="button"
              onClick={handleCloseModal}
              className="inline-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting}
              disabled={loading}
              data-cy="review-confirm-rejection"
            >
              Send to prospect
            </Button>
          </Flex>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
